import { ModalBaseComponent } from '@/components/base/modal-base/modal-base.component';
import { DocAddWidgetComponent } from '@/components/doc-add-widget/doc-add-widget.component';
import { InputComponent } from '@/components/ui-forms/input/input.component';
import { BookletBuildingStageTranslatePipe } from '@/extensions/booklet--building-stage-translate.pipe';
import { BuildingDocTypeTranslatePipe } from '@/extensions/booklet-building-type-translate.pipe';
import { PropertyBookletBuildingType } from '@/views/property/property-booklet/property-booklet.component';
import { NgTemplateOutlet } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component, Inject, OnDestroy, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DocumentSummary, PropertiesBookletService, PropertyBookletBuilding, PropertyBookletBuildingDocumentType, PropertyBookletBuildingStage, RealProperty } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { PropertyBookletBuildingService } from '../property-booklet-building-card/property-booklet-building.service';

export class PropertyBookletBuildingDialogData {
  componentType: PropertyBookletBuildingType;
  bookletUid: string;
  prop: RealProperty;
}

interface BuildingDocumentFormData {
  stage: PropertyBookletBuildingStage;
  documentType: PropertyBookletBuildingDocumentType;
  docSummary: DocumentSummary;
}

@Component({
  selector: 'app-property-booklet-building-dialog',
  standalone: true,
  imports: [ModalBaseComponent, MatDialogModule, ReactiveFormsModule, InputComponent, NgTemplateOutlet,
            DocAddWidgetComponent, HttpClientModule, BuildingDocTypeTranslatePipe, BookletBuildingStageTranslatePipe],
  templateUrl: './property-booklet-building-dialog.component.html',
  styleUrl: './property-booklet-building-dialog.component.scss',
  providers: [PropertyBookletBuildingService]
})
export class PropertyBookletBuildingDialogComponent implements OnDestroy {

  private readonly fb = inject(FormBuilder);
  private readonly propertyBookletBuildingService = inject(PropertyBookletBuildingService);
  private readonly propertiesBookletService = inject(PropertiesBookletService);

  title: string;
  componentType: PropertyBookletBuildingType;
  bookletUid: string;
  prop: RealProperty;
  isLoading: boolean;
  sub = new Subscription();
  arePopulateData = false;

  get form_startDate() {
    return this.form.get('startDate') as FormControl<string | null>;
  }
  get form_deliveryDate() {
    return this.form.get('deliveryDate') as FormControl<string | null>;
  }
  get f_documents() {
    return this.form.get('documents') as FormArray<FormControl<BuildingDocumentFormData>>;
  }
  form = this.fb.group({
    // FIXME: the start and delivery date are note set for each component but globally
    // remove the required validator to disable the fields for now
    startDate: [null, []],
    deliveryDate: [null, []],
    documents: this.fb.array<FormControl<BuildingDocumentFormData>>([])
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PropertyBookletBuildingDialogData,
    public dialogRef: MatDialogRef<PropertyBookletBuildingDialogComponent>,
  ) {
    this.componentType = data.componentType;
    this.bookletUid = data.bookletUid;
    this.prop = data.prop;
    this.title = PropertyBookletBuildingService.getTitle(this.componentType);

    this.initForm();
  }

  onSubmit() {
    if (this.form.valid) {
      this.update();
    }
  }

  initForm() {
    this.propertyBookletBuildingService.requiredDocByType.get(this.componentType)?.forEach(type => {
      this.f_documents.push(this.createDocumentControl(type, PropertyBookletBuildingStage.DESIGN));
      this.f_documents.push(this.createDocumentControl(type, PropertyBookletBuildingStage.EXECUTION));
    })

    this.populateData()
  }

  createDocumentControl(type: PropertyBookletBuildingDocumentType, stage: PropertyBookletBuildingStage): FormControl<BuildingDocumentFormData> {
    return this.fb.control<BuildingDocumentFormData>({
      stage: stage,
      documentType: type,
      docSummary: null
    });
  }

  setFormFromData(resp: PropertyBookletBuilding) {
    this.form_startDate.setValue(resp?.startDate ?? null);
    this.form_deliveryDate.setValue(resp?.deliveryDate ?? null);
    if (resp) {
      this.f_documents.controls.forEach(doc => {
        const found = resp[this.getAttribute(this.componentType)]?.find(docData => docData.type === doc.value.documentType &&
                                                                        docData.stage === doc.value.stage);
        if (found) {
          doc.setValue({
            stage: found.stage,
            documentType: found.type,
            docSummary: found.document
          })
        }
      });
    }
  }
  getAttribute(type: PropertyBookletBuildingType): string {
    switch (type) {
      case PropertyBookletBuildingType.housing:
        return 'housing';
      case PropertyBookletBuildingType.surface:
        return 'surface';
      case PropertyBookletBuildingType.waterNetwork:
        return 'waterNetwork';
      case PropertyBookletBuildingType.electricityNetwork:
        return 'electricityNetwork';
      case PropertyBookletBuildingType.gasNetwork:
        return 'gasNetwork';
      case PropertyBookletBuildingType.ventilationNetwork:
        return 'ventilationNetwork';
    }
  }

  update() {
    this.isLoading = true;
    const body = this.getBody();
    this.sub = this.propertiesBookletService.updateRealPropertyBookletBuilding(this.prop.uid, this.bookletUid, body).subscribe({
      next: () => {
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }
  getBody(): PropertyBookletBuilding {
    const t = this.f_documents.value
    return {
      startDate: this.form_startDate.value,
      deliveryDate: this.form_deliveryDate.value,
      [this.getAttribute(this.componentType)]: this.f_documents.value.map(doc => {
        if (doc.docSummary) {
          return {
            stage: doc.stage,
            type: doc.documentType,
            document: {
               uid: doc.docSummary.uid,
               type: doc.docSummary.type
            }
          }
        }
      })
    }
  }

  onDocSelected(newDoc: DocumentSummary, index: number) {
    const d = this.f_documents.at(index);
    d.setValue({
      ...d.value,
      docSummary: newDoc
    })
  }

  onDocDeleted(docToDelete: DocumentSummary, index: number) {
    const d = this.f_documents.at(index);
    d.setValue({
      ...d.value,
      docSummary: null
    })
  }

  populateData() {
    this.isLoading = true;

    this.sub = this.propertiesBookletService.getRealPropertyBookletBuilding(this.prop.uid, this.bookletUid).subscribe({
      next: (resp) => {
        this.isLoading = false;
        this.setFormFromData(resp);
      },
      error: () => {
        this.isLoading = false;
      },
      complete: () => {
        this.arePopulateData = true;
      }
    });
  }

  ngOnDestroy(): void {
      this.sub.unsubscribe();
  }
}
